import { useMemo } from 'react';
import { usePermissions } from '@features/entity/hooks/usePermissions';
import { getScopeFromValue } from '@features/invitations/utils/getScopeFromParam';
import {
  getReportActionCodesForScope,
  getScopedActionCode,
  getActivityEntryActionCodesForScope,
  getScopedActivityEntryCode,
} from '@features/entity/types/actionCodes';
import type { ReportActionCapabilities } from '@core/reports/utils/reportPermissionFlags';

type ReportScope = 'church' | 'district' | 'nation';

export interface UseReportActionPermissionsArgs {
  scope: ReportScope | string;
  scopeId: number;
  departmentId: number;
  /** Include activity-entry action codes (church activity submission form). */
  includeActivityEntry?: boolean;
  enabled?: boolean;
}

export function useReportActionPermissions({
  scope,
  scopeId,
  departmentId,
  includeActivityEntry = false,
  enabled = true,
}: UseReportActionPermissionsArgs) {
  const normalizedScope = useMemo(() => getScopeFromValue(scope), [scope]);
  const ownApproveCode = useMemo(
    () => getScopedActionCode(normalizedScope, 'APPROVE_OWN_REPORT'),
    [normalizedScope]
  );

  const actionCodes = useMemo(() => {
    const codes = getReportActionCodesForScope(normalizedScope);
    if (includeActivityEntry) {
      return [...codes, ...getActivityEntryActionCodesForScope(normalizedScope)];
    }
    return codes;
  }, [normalizedScope, includeActivityEntry]);

  const { hasPermission, loading, error, refresh, permittedPermissions } = usePermissions({
    scopeType: enabled && scopeId > 0 && departmentId > 0 ? normalizedScope : undefined,
    scopeId: enabled && scopeId > 0 ? scopeId : undefined,
    departmentId: enabled && departmentId > 0 ? departmentId : undefined,
    actionCodes,
  });

  const ownAccessEnabled = enabled && scopeId > 0 && Boolean(ownApproveCode);
  const { hasPermission: hasOwnPermission, loading: ownLoading } = usePermissions({
    scopeType: ownAccessEnabled ? normalizedScope : undefined,
    scopeId: ownAccessEnabled ? scopeId : undefined,
    actionCodes: ownApproveCode ? [ownApproveCode] : [],
  });

  const capabilities: ReportActionCapabilities = useMemo(
    () => ({
      canSave: hasPermission(getScopedActionCode(normalizedScope, 'SAVE_REPORT')),
      canSubmit: hasPermission(getScopedActionCode(normalizedScope, 'SUBMIT_REPORT')),
      canReview: hasPermission(getScopedActionCode(normalizedScope, 'REVIEW_REPORT')),
      canApprove: hasPermission(getScopedActionCode(normalizedScope, 'APPROVE_REPORT')),
      canApproveOwn: hasOwnPermission(ownApproveCode),
      canValidate: hasPermission(getScopedActionCode(normalizedScope, 'VALIDATE_REPORT')),
      canUploadMedia: hasPermission(getScopedActionCode(normalizedScope, 'UPLOAD_MEDIA')),
      canRemoveMedia: hasPermission(getScopedActionCode(normalizedScope, 'REMOVE_MEDIA')),
    }),
    [hasPermission, hasOwnPermission, normalizedScope, ownApproveCode]
  );

  const activityCapabilities = useMemo(
    () => ({
      canSaveActivity: hasPermission(getScopedActivityEntryCode(normalizedScope, 'save')),
      canSubmitActivity: hasPermission(getScopedActivityEntryCode(normalizedScope, 'submit')),
      canReviewActivity: hasPermission(getScopedActivityEntryCode(normalizedScope, 'review')),
    }),
    [hasPermission, normalizedScope]
  );

  return {
    loading: enabled ? loading || ownLoading : false,
    error,
    refresh,
    permittedPermissions,
    ...capabilities,
    ...activityCapabilities,
  };
}
